import { useEffect, useMemo, useState } from "react";
import { Check, ArrowRight, Clock, ClipboardList, CalendarCheck, MessageCircle, Link as LinkIcon, Bell, HandCoins, CalendarPlus, Copy, Share2 } from "lucide-react";
import { SETUP_STEPS, getSetupStatus } from "../setupSteps";
import { buildBusinessUrl } from "../../../utils/businessUrl";
import "./guia.css";

/**
 * Sección "guide" del panel. Mientras falte configuración es "Primeros pasos": el
 * checklist de los 4 pasos con por qué, cómo y un botón a la pestaña donde se hace
 * (el modal obligatorio restringe; esta pantalla explica). Al completarlos se vuelve
 * "Así funciona": el link público para compartir y qué pasa ahora en la agenda,
 * con un atajo a cada pestaña.
 *
 * `setupPending` viene de page.jsx (mismo dato que abre el modal) para que el modo
 * coincida siempre con la etiqueta del menú; el estado de cada paso se calcula
 * con el clientData más reciente (se refresca al entrar, porque Servicios y
 * Horarios guardan sin refrescarlo).
 */
export default function GuideTab({ clientData, setupPending, refetchClientData, onGo }) {
  useEffect(() => { refetchClientData?.(); }, [refetchClientData]);
  if (!clientData) return null;
  return setupPending
    ? <FirstSteps clientData={clientData} onGo={onGo} />
    : <HowItWorks clientData={clientData} onGo={onGo} />;
}

const STEP_COPY = {
  schedule: {
    icon: Clock,
    why: "Solo se ofrecen citas dentro de tu horario. Sin él, tu agenda aparece vacía.",
    how: "Marca los días que atiendes y la hora de cada uno. Puedes partir el día para dejar fuera la comida.",
  },
  services: {
    icon: ClipboardList,
    why: "Es lo que tus clientes eligen al agendar. La duración define cuánto ocupa cada cita.",
    how: "Nombre, precio y duración es lo mínimo. Si pides anticipo, actívalo en el servicio.",
  },
  google: {
    icon: CalendarCheck,
    why: "Ahí se guardan las citas y de ahí se revisa qué horas siguen libres.",
    how: "En Conexiones, conecta el calendario de al menos un profesional con su cuenta de Google.",
  },
  whatsapp: {
    icon: MessageCircle,
    why: "Es el número por el que el bot atiende a tus clientes y manda los recordatorios.",
    how: "En Conexiones, vincula el WhatsApp de tu negocio escaneando el código o con el código de vinculación.",
  },
};

function FirstSteps({ clientData, onGo }) {
  const status = getSetupStatus(clientData);
  return (
    <section className="cp-guide" aria-labelledby="cp-guide-title">
      <header className="cp-guide-head">
        <p className="cp-eyebrow">Primeros pasos</p>
        <h2 className="cp-guide-title" id="cp-guide-title">Deja tu agenda lista</h2>
        <p className="cp-guide-lead">Cuatro pasos y tus clientes podrán agendar solos, por WhatsApp o por tu link.</p>
        <div className="cp-guide-progress">
          <div className="cp-guide-progress-bar" role="progressbar" aria-label="Pasos completados" aria-valuemin={0} aria-valuemax={status.total} aria-valuenow={status.completed}>
            <span style={{ width: `${(status.completed / status.total) * 100}%` }} />
          </div>
          <span className="cp-guide-progress-label">{status.completed} de {status.total}</span>
        </div>
      </header>

      <ol className="cp-guide-steps">
        {SETUP_STEPS.map((step, i) => {
          const done = status.done[i];
          const current = i === status.currentIndex;
          const copy = STEP_COPY[step.key];
          const Icon = copy.icon;
          return (
            <li key={step.key} className={`cp-guide-step${done ? " is-done" : ""}${current ? " is-current" : ""}`} data-tour={current ? "guia.current" : undefined}>
              <div className="cp-guide-step-mark" aria-hidden="true">{done ? <Check size={16} strokeWidth={3} /> : i + 1}</div>
              <div className="cp-guide-step-body">
                <div className="cp-guide-step-top">
                  <Icon size={18} className="cp-guide-step-icon" />
                  <h3>{step.label}</h3>
                  {done && <span className="cp-guide-tag is-done">Listo</span>}
                  {current && <span className="cp-guide-tag">Siguiente</span>}
                </div>
                {!done && (
                  <>
                    <p className="cp-guide-step-why">{copy.why}</p>
                    <p className="cp-guide-step-how"><strong>Cómo:</strong> {copy.how}</p>
                    <button type="button" className={`cp-btn${current ? " cp-btn-primary" : ""}`} onClick={() => onGo(step.tab)}>
                      Hacerlo ahora <ArrowRight size={16} />
                    </button>
                  </>
                )}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

function HowItWorks({ clientData, onGo }) {
  const url = useMemo(() => buildBusinessUrl(clientData.slug), [clientData.slug]);
  const name = clientData.business?.name || "mi negocio";
  const shareText = `Agenda tu cita en ${name}: ${url}`;
  const canShare = typeof navigator !== "undefined" && typeof navigator.share === "function";
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(t);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch {
      window.prompt("Copia tu link:", url); // sin permiso de portapapeles (http, iframe): que lo copie a mano
    }
  };
  const share = async () => {
    try { await navigator.share({ title: name, text: shareText, url }); } catch { /* canceló */ }
  };

  const hasAdvance = clientData.business?.services?.some((s) => s.requiresAdvance);
  const items = [
    { icon: MessageCircle, title: "Agendan por WhatsApp", body: "Cuando alguien escribe a tu número, el bot saluda, muestra tus servicios y las horas libres, y agenda la cita solo. Tú te enteras al momento.", tab: "bot", go: "Ajustar el bot" },
    { icon: LinkIcon, title: "Agendan por tu link", body: "En tu página eligen servicio, profesional y hora. La cita se guarda en tu Google Calendar y el cliente recibe su confirmación por WhatsApp.", tab: "colors", go: "Personalizar tu página" },
    { icon: Bell, title: "Recordatorios automáticos", body: "Antes de cada cita el bot le recuerda al cliente. Tú eliges cuánto antes y con qué texto; es lo que más reduce las citas perdidas.", tab: "bot", go: "Configurar recordatorios" },
    hasAdvance
      ? { icon: HandCoins, title: "Anticipos", body: "Las citas de servicios con anticipo quedan pendientes hasta que confirmes el comprobante en Citas. Entonces se vuelven definitivas y el cliente recibe su confirmación.", tab: "appointments", go: "Ver anticipos pendientes" }
      : { icon: HandCoins, title: "Anticipos", body: "Si un servicio pide anticipo, la cita queda pendiente hasta que confirmes el comprobante. Actívalo por servicio, para los que te dejan plantado si no aseguran su lugar.", tab: "services", go: "Pedir anticipo en un servicio" },
    { icon: CalendarPlus, title: "Tú también puedes agendar", body: "Para citas que llegan por teléfono o en mostrador: Nueva cita, en Citas. Si pones el número del cliente, recibe confirmación y recordatorios como cualquier otra.", tab: "appointments", go: "Ir a Citas" },
  ];

  return (
    <section className="cp-guide" aria-labelledby="cp-guide-title">
      <header className="cp-guide-head">
        <p className="cp-eyebrow">Así funciona</p>
        <h2 className="cp-guide-title" id="cp-guide-title">Tu agenda ya está funcionando</h2>
        <p className="cp-guide-lead">Todo está conectado. Esto es lo que pasa ahora y dónde ajustarlo.</p>
      </header>

      <div className="cp-guide-share" data-tour="guia.link">
        <p className="cp-eyebrow">Tu link para agendar</p>
        <a className="cp-guide-url" href={url} target="_blank" rel="noreferrer">{url.replace(/^https?:\/\//, "")}</a>
        <div className="cp-guide-actions">
          <button type="button" className="cp-btn cp-btn-primary" onClick={copy} aria-live="polite">
            {copied ? <Check size={16} /> : <Copy size={16} />} {copied ? "Copiado" : "Copiar"}
          </button>
          <a className="cp-btn" href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noreferrer">
            <MessageCircle size={16} /> WhatsApp
          </a>
          {canShare && (
            <button type="button" className="cp-btn" onClick={share}>
              <Share2 size={16} /> Compartir
            </button>
          )}
        </div>
        <p className="cp-muted">Ponlo en tu Instagram, en la bio de tu WhatsApp o mándaselo a tus clientes.</p>
      </div>

      <ul className="cp-guide-list">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <li key={it.title} className="cp-guide-item">
              <div className="cp-guide-item-icon" aria-hidden="true"><Icon size={18} /></div>
              <div className="cp-guide-item-body">
                <h3>{it.title}</h3>
                <p>{it.body}</p>
                <button type="button" className="cp-guide-go" onClick={() => onGo(it.tab)}>
                  {it.go} <ArrowRight size={14} />
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
